/**
 * Strategic resources in words (ADR-0063): what a city has, and what an empire has and
 * lacks. Shared by the city screen and the cities screen so they say the same thing.
 */

import type { Content } from '../../content/schema/index.js';
import { resourceAccess } from '../core/resources/access.js';
import { resourceKnown, resourceNames } from '../core/resources/known.js';
import type { City, GameState, Id } from '../core/state/types.js';

/** "Horses, Iron (traded)" — or why there are none. */
export function resourceLine(state: GameState, content: Content, city: City): string {
  const access = resourceAccess(state, content, city.owner);
  const mask = access.cities.get(city.id) ?? 0;
  if (mask === 0) {
    const waiting = access.deposits.filter((d) => !d.connected).length;
    return waiting > 0 ? `none joined — ${waiting} deposit${waiting === 1 ? '' : 's'} in your lands need a road` : 'none';
  }
  const own = mask & ~access.traded;
  const traded = mask & access.traded & ~own;
  return [...resourceNames(content, own), ...resourceNames(content, traded).map((n) => `${n} (traded)`)].join(', ');
}

export interface EmpireResource {
  name: string;
  /** 'have': joined somewhere or traded in; 'unjoined': in your land, no road; 'lacking': known, none held; 'hidden': not yet revealed. */
  status: 'have' | 'traded' | 'unjoined' | 'lacking' | 'hidden';
  /** The technology that reveals it, when hidden. */
  revealedBy: string | null;
  /** The standard units that need it. */
  usedBy: string[];
}

/** Every strategic resource and where the empire stands with it. */
export function empireResources(state: GameState, content: Content, civId: Id): EmpireResource[] {
  const civ = state.civs[civId];
  const access = resourceAccess(state, content, civId);
  return content.resources.all
    .filter((r) => r.class === 'strategic')
    .map((r) => {
      const bit = 1 << r.id;
      let status: EmpireResource['status'];
      if (!resourceKnown(content, civ, r.id)) status = 'hidden';
      else if (access.own & bit) status = 'have';
      else if (access.traded & bit) status = 'traded';
      else if (access.deposits.some((d) => d.resource === r.id)) status = 'unjoined';
      else status = 'lacking';
      const usedBy = content.units.all.filter((u) => u.civId === -1 && u.resourceIds.includes(r.id)).map((u) => u.name);
      return { name: r.name, status, revealedBy: r.revealedId >= 0 ? content.technologies.get(r.revealedId).name : null, usedBy };
    });
}
